import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Field, useField, useForm, FieldProps, FieldRenderProps } from 'react-final-form';
import colors from 'constants/colors';
import Icon, { IconNames } from 'components/Icon';
import { InputWithFloatingLabel } from './InputWithFloatingLabel';

const getGroupedOptions = (value: ValueType, options: Option[]) => {
  const activeOption = options.find(option => option.value === value);
  const inactiveOptions = options.filter(option => option.value !== value);
  return { activeOption, inactiveOptions };
};

type ValueType = string | number;

interface Option {
  label: ReactNode;
  value: ValueType;
}

interface OptionGroup {
  label: string;
  options: Option[];
}

interface Props extends FieldProps<HTMLInputElement, FieldRenderProps<any>> {
  label?: ReactNode;
  name: string;
  options?: Option[];
  groups?: OptionGroup[];
  disabled?: boolean;
}

export const SelectField: FC<Props> = ({
  label,
  name,
  options = [],
  groups,
  validate,
  onChange,
  disabled,
  ...fieldProps
}) => {
  const [showOptions, setShowOptions] = useState(false);

  const { change: changeForm } = useForm();
  const {
    input: { value },
    meta: { error, submitError, touched },
  } = useField(name, {
    subscription: { value: true, error: true, submitError: true, touched: true },
  });
  const allOptions = groups ? groups.flatMap(g => g.options) : options;
  const { activeOption, inactiveOptions } = getGroupedOptions(value, allOptions);

  const comboboxRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  const onSelectToggle = () => {
    if (disabled) return;
    setShowOptions(showOptions => !showOptions);
  };

  const selectValue = useCallback(
    (optValue: ValueType) => {
      if (onChange) onChange(optValue);
      changeForm(name, optValue);
      setShowOptions(false);
      comboboxRef.current?.focus();
    },
    [onChange, changeForm, name]
  );

  const getOnClickOption = (optValue: ValueType) => {
    return () => selectValue(optValue);
  };

  const focusOption = useCallback((direction: 'first' | 'next' | 'prev') => {
    const container = listboxRef.current;
    if (!container) return;
    const optionElements = container.querySelectorAll<HTMLElement>('[role="option"]');
    if (optionElements.length === 0) return;

    const currentIndex = Array.from(optionElements).findIndex(el => el === document.activeElement);

    let targetIndex: number;
    if (direction === 'first') {
      targetIndex = 0;
    } else if (direction === 'next') {
      targetIndex = currentIndex < optionElements.length - 1 ? currentIndex + 1 : currentIndex;
    } else {
      targetIndex = currentIndex > 0 ? currentIndex - 1 : 0;
    }

    optionElements[targetIndex]?.focus();
  }, []);

  useEffect(() => {
    if (showOptions) {
      requestAnimationFrame(() => focusOption('first'));
    }
  }, [showOptions, focusOption]);

  const handleComboboxKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!showOptions) {
      if (['Enter', ' ', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        setShowOptions(true);
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setShowOptions(false);
      comboboxRef.current?.focus();
    }
  };

  const handleOptionKeyDown = (optValue: ValueType) => (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        selectValue(optValue);
        break;
      case 'Escape':
        e.preventDefault();
        setShowOptions(false);
        comboboxRef.current?.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        focusOption('next');
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusOption('prev');
        break;
    }
  };

  const labelText = typeof label === 'string' ? label : name;

  return (
    <div className="select-field-container">
      <Field component="select" name={name} hidden validate={validate} {...fieldProps}>
        {allOptions.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Field>
      <InputWithFloatingLabel
        className={classNames({
          active: showOptions,
          'dropdown-open': showOptions,
          error: touched && (error || submitError),
        })}
        error={touched && (error || submitError)}
        label={label}
        value={activeOption?.value}
      >
        <div
          className="row"
          ref={comboboxRef}
          onClick={onSelectToggle}
          onKeyDown={handleComboboxKeyDown}
          role="combobox"
          aria-expanded={showOptions}
          aria-haspopup="listbox"
          aria-label={labelText}
          tabIndex={0}
        >
          <span className="bodySmall input" style={{ width: '100%' }}>
            {activeOption?.label || (!label && '--')}
          </span>
          <div className={classNames('select-toggle-icon', { open: showOptions })}>
            <Icon
              name={showOptions ? IconNames.ChevronUp : IconNames.ChevronDown}
              color={colors.white}
              size={10}
            />
          </div>
        </div>

        {showOptions && (
          <div>
            <div className="select-options-container" role="listbox" ref={listboxRef}>
              {groups
                ? groups.map((group, groupIndex) => {
                    const groupInactive = group.options.filter(
                      o => o.value !== activeOption?.value
                    );
                    if (groupInactive.length === 0) return null;
                    return (
                      <div key={group.label}>
                        {groupIndex > 0 && <hr className="select-group-divider" />}
                        <div className="select-group-header" role="presentation">
                          {group.label}
                        </div>
                        {groupInactive.map(({ label: optLabel, value: optValue }) => (
                          <div
                            className="select-option"
                            key={optValue}
                            id={`${name}-option-${optValue}`}
                            onClick={getOnClickOption(optValue)}
                            onKeyDown={handleOptionKeyDown(optValue)}
                            role="option"
                            aria-selected={optValue === value}
                            tabIndex={0}
                          >
                            <span className="bodySmall">{optLabel}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })
                : inactiveOptions.map(({ label: optLabel, value: optValue }) => (
                    <div
                      className="select-option"
                      key={optValue}
                      id={`${name}-option-${optValue}`}
                      onClick={getOnClickOption(optValue)}
                      onKeyDown={handleOptionKeyDown(optValue)}
                      role="option"
                      aria-selected={optValue === value}
                      tabIndex={0}
                    >
                      <span className="bodySmall">{optLabel}</span>
                    </div>
                  ))}
            </div>
          </div>
        )}
      </InputWithFloatingLabel>
    </div>
  );
};
